import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { LikesDataService, LikesColumnsService } from '../../services';
import { DialogLikeFormComponent } from './like-form/like-form.component';
import { DialogLikeDetailsComponent } from './like-details/like-details.component';
import { Like, LikesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-likes',
  templateUrl: './likes.component.html',
  styleUrls: ['./likes.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class LikesComponent implements OnInit, OnDestroy {

  private likeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private likeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private likeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public likeToDelete: Like = {};
  public likes: Like[] = [];
  public like: Like = {};
  public selectedLikes: Like[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private likesDataService: LikesDataService,
    private likesColumnsService: LikesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('LikesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Likes - Management');
    this.primengConfig.ripple = true;
    this.getLikes();
  }

  ngOnDestroy(): void {
    console.log('LikesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getLikes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.likesDataService.getLikes(this.parameters).subscribe((response: LikesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.likes = response.results;
        } else {
          this.likes = this.likes.concat(response.results);
        }
        if (response.inlineCount > this.likes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getLikes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIKESNOTLOADED') });
    });
  }

  importLikes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.likesDataService.addLikes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LIKESCREATED') });
                if (proceed === j) {
                  this.getLikes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIKESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIKESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.likesDataService.updateLikes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('LIKESUPDATED') });
                if (proceed === j) {
                  this.getLikes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIKESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('LIKESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.likesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('likes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedLikes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Likes?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.likesDataService.deleteLikes(this.selectedLikes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Likes Deleted', life: 3000 });
            this.getLikes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Likes not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Likes not deleted' });
        });
        this.selectedLikes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.likes, this.columns, 'Likes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.likes, this.columns, 'Likes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.likes, this.columns, 'Likes');
  }

  refresh() {
    this.likesDataService.clearCache();
    this.getLikes();
  }

  likeAdd(): void {
    if (!this.navigation.getParamValue('likes.addInDialog', true)) {
      this.router.navigate(['/likes/add']);
      return;
    }
    this.likeAddDialog = this.dialogService.open(DialogLikeFormComponent, {
      header: '', // this.translate.instant('NEWLIKE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        like: { id: null },
        users: this.likesColumnsService.users,
      },
    });

    this.likeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'Like added' });
        this.getLikes();
      }
    });
  }

  likeEdit(like: Like): void {
    if (!this.navigation.getParamValue('likes.editInDialog', true)) {
      this.router.navigate(['/likes/edit/' + like.id]);
      return;
    }
    this.likeEditDialog = this.dialogService.open(DialogLikeFormComponent, {
      header: '', // this.translate.instant('EDITLIKE') + ' ' + like.entity,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        like: like,
        users: this.likesColumnsService.users,
      },
    });

    this.likeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Like updated' });
        this.getLikes();
      }
    });
  }

  likeDetails(like: Like): void {
    if (!this.navigation.getParamValue('likes.detailsInDialog', true)) {
      this.router.navigate(['/likes/details/' + like.id]);
      return;
    }
    this.likeDetailsDialog = this.dialogService.open(DialogLikeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        like: like,
      }
    });
  }

  likeDelete(like: Like): void {
    this.likeToDelete = like;
    this.messageService.clear();
    this.messageService.add({
      key: 'likeDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + like.entity + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.likesDataService.deleteLike(this.likeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Like deleted' });
      this.getLikes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Like not deleted' });
    });
  }

  onDeleteReject(): void {
    this.likeToDelete = {};
    this.messageService.clear('likeDelete');
  }

}
